import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Region = 'US' | 'CA' | 'OTHER';
export type Currency = 'USD' | 'CAD';

interface RegionContextType {
  region: Region;
  currency: Currency;
  isLoading: boolean;
}

const RegionContext = createContext<RegionContextType | undefined>(undefined);

interface RegionProviderProps {
  children: ReactNode;
}

export const RegionProvider: React.FC<RegionProviderProps> = ({ children }) => {
  const [region, setRegion] = useState<Region>('CA');
  const [currency, setCurrency] = useState<Currency>('CAD');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const detectRegion = async () => {
      try {
        // Check for manual region override in localStorage (for testing)
        const manualRegion = localStorage.getItem('test_region') as Region | null;
        if (manualRegion && (manualRegion === 'US' || manualRegion === 'CA' || manualRegion === 'OTHER')) {
          setRegion(manualRegion);
          setCurrency(manualRegion === 'US' ? 'USD' : 'CAD');
          setIsLoading(false);
          console.log('🧪 Using test region:', manualRegion);
          return;
        }

        // Try to fetch region from API
        const response = await fetch('/api/detect-region');
        if (response.ok) {
          const data = await response.json();
          const detectedRegion: Region = data.region || 'CA';
          setRegion(detectedRegion);

          // Set currency based on region
          if (detectedRegion === 'US') {
            setCurrency('USD');
          } else {
            setCurrency('CAD');
          }
          console.log('🌍 Detected region:', detectedRegion);
        }
      } catch (error) {
        console.error('Error detecting region:', error);
        // Default to Canada
        setRegion('CA');
        setCurrency('CAD');
      } finally {
        setIsLoading(false);
      }
    };

    detectRegion();
  }, []);

  const value: RegionContextType = {
    region,
    currency,
    isLoading,
  };

  return (
    <RegionContext.Provider value={value}>
      {children}
    </RegionContext.Provider>
  );
};

export const useRegion = (): RegionContextType => {
  const context = useContext(RegionContext);
  if (context === undefined) {
    throw new Error('useRegion must be used within a RegionProvider');
  }
  return context;
};
